import { Injectable, signal } from '@angular/core';
import {
    BabylonBreadcrumbI,
    BabylonEngineI,
    BabylonFooterContactI,
    BabylonFooterLinksI,
    BabylonFooterLogosI,
    BabylonGlobalModalI,
    BabylonHeaderBookShowI,
    BabylonHeaderClearI,
    BabylonHeaderMenuCenterI,
    BabylonHeaderMenuLogoCenterI,
    BabylonHeaderMenuShowI,
    BabylonModalService,
    BabylonOffersListI,
    BabylonStaticFooterI,
    BabylonStaticFooterV2I,
    BabylonSubmenuI,
    BabylonTopSliderI,
    BabylonTopSliderThumbsI,
    BabylonTopSliderVideoI,
    BodyComponent,
    COMPONENTS_EXTRA,
    PageConfig,
    PageConfigHead,
    Utils,
} from 'ctt-babylon';

export type BabylonHeadersType =
    | 'headerBookShow'
    | 'headerClear'
    | 'headerMenuCenter'
    | 'headerMenuShow'
    | 'headerCenterBookShow'
    | 'headerClearEngineShow'
    | 'headerMenuShowEngineShow';

@Injectable({
    providedIn: 'root',
})
export class LayoutService {
    private readonly headerData = signal<
        | BabylonHeaderBookShowI
        | BabylonHeaderClearI
        | BabylonHeaderMenuCenterI
        | BabylonHeaderMenuShowI
        | undefined
        // BabylonHeaderBookShowI | undefined
    >(undefined);

    readonly footer = signal<
        BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I> | undefined
    >(undefined);

    private readonly engineData = signal<BabylonEngineI | undefined>(undefined);

    private readonly pageHead = signal<PageConfigHead | undefined>(undefined);

    readonly dynamicHeader = signal<boolean>(false);

    readonly pageDataLoaded = signal<boolean>(false);

    private readonly loadFooter = signal<boolean>(false);

    private readonly loadHeaders = signal<boolean>(false);

    private readonly advantagesUp = signal<boolean>(false);

    private readonly selectedHeader = signal<BabylonHeadersType>('headerClear');

    readonly breadcrumbs = signal<BabylonBreadcrumbI | undefined>(undefined);

    private readonly globalModal = signal<any[] | undefined>(undefined);

    readonly offersList = signal<BodyComponent<BabylonOffersListI> | undefined>(
        undefined
    );

    readonly topSlider = signal<
        | BodyComponent<
              | BabylonTopSliderI
              | BabylonTopSliderVideoI
              | BabylonTopSliderThumbsI
          >
        | undefined
    >(undefined);

    readonly footerLogos = signal<
        BodyComponent<BabylonFooterLogosI> | undefined
    >(undefined);
    readonly footerLinks = signal<
        BodyComponent<BabylonFooterLinksI> | undefined
    >(undefined);
    readonly footerContact = signal<
        BodyComponent<BabylonFooterContactI> | undefined
    >(undefined);

    private readonly engineAlreadyExists = signal<boolean>(false);

    readonly submenu = signal<BabylonSubmenuI | undefined>(undefined);

    readonly firstPageLoaded = signal<boolean>(false);

    get headerDataInstant():
        | BabylonHeaderBookShowI
        | BabylonHeaderClearI
        | BabylonHeaderMenuCenterI
        | BabylonHeaderMenuShowI
        | BabylonHeaderMenuLogoCenterI
        | undefined {
        return this.headerData();
    }

    get engineDataInstant(): BabylonEngineI | undefined {
        return this.engineData();
    }

    get headInstant(): PageConfigHead | undefined {
        return this.pageHead();
    }

    get footerLinksDataInstant():
        | BodyComponent<BabylonFooterLinksI>
        | undefined {
        return this.footerLinks();
    }

    get footerLogosDataInstant():
        | BodyComponent<BabylonFooterLogosI>
        | undefined {
        return this.footerLogos();
    }

    get footerContactDataInstant():
        | BodyComponent<BabylonFooterContactI>
        | undefined {
        return this.footerContact();
    }

    get offersListDataInstant(): BodyComponent<BabylonOffersListI> | undefined {
        return this.offersList();
    }

    get dynamicHeaderInstant(): boolean {
        return this.dynamicHeader();
    }

    get loadFooterInstant(): boolean {
        return this.loadFooter();
    }

    get loadHeadersInstant(): boolean {
        return this.loadHeaders();
    }

    get advantagesUpInstant(): boolean {
        return this.advantagesUp();
    }

    get selectedHeaderInstant(): BabylonHeadersType {
        return this.selectedHeader();
    }

    get engineAlreadyExistsInstant(): boolean {
        return this.engineAlreadyExists();
    }

    get footerInstant():
        | BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I>
        | undefined {
        return this.footer();
    }

    setLayoutElements<T>(
        response?: PageConfig<T | undefined>,
        isHome?: boolean
    ): void {
        if (response) {
            this.setHead(response.head);
            this.dynamicHeader.set(!!isHome);

            const topSliderComp = Utils.findComponent(
                response.body,
                'topslider'
            );
            const topSliderVideoComp = Utils.findComponent(
                response.body,
                'topslidervideo'
            );

            this.topSlider.set(
                topSliderComp
                    ? {
                          name: 'topslider',
                          props: topSliderComp as BabylonTopSliderI,
                      }
                    : topSliderVideoComp
                      ? {
                            name: 'topslidervideo',
                            props: topSliderVideoComp as BabylonTopSliderVideoI,
                        }
                      : undefined
            );

            this.breadcrumbs.set(
                response.bodyExtra
                    ? (Utils.findComponent<COMPONENTS_EXTRA>(
                          response.bodyExtra,
                          'breadcrumbs'
                      ) as BabylonBreadcrumbI)
                    : undefined
            );

            this.advantagesUp.set(
                Utils.findComponent(response.body, 'advantages') !== undefined
            );
            this.pageDataLoaded.set(true);
            this.loadFooter.set(true);
            this.loadHeaders.set(true);

            this.submenu.set(
                response.bodyExtra
                    ? (Utils.findComponent<COMPONENTS_EXTRA>(
                          response.bodyExtra,
                          'menu'
                      ) as BabylonSubmenuI)
                    : undefined
            );

            const globalModals = (response.body ?? [])
                .filter((component) => component.name === 'globalmodal')
                .map((component) => ({
                    ...component,
                    name: component.alias?.toLowerCase(),
                }));
            this.globalModal.set(globalModals);

            const offersList = Utils.findComponent(
                response.body,
                'offerslist'
            ) as BabylonOffersListI | undefined;
            const safeOffersList = offersList?.items
                ? offersList
                : { ...offersList, items: [] };
            const filteredItems = isHome
                ? safeOffersList.items?.filter(
                      (offer: any) => offer.home === 1 || offer.home === '1'
                  )
                : safeOffersList.items;
            const filteredOffersList: BodyComponent<BabylonOffersListI> = {
                name: 'offerslist',
                props: {
                    ...safeOffersList,
                    items: filteredItems,
                },
            };
            this.offersList.set(filteredOffersList);
        }
    }

    setheaderData(
        headerData:
            | BabylonHeaderBookShowI
            | BabylonHeaderClearI
            | BabylonHeaderMenuCenterI
            | BabylonHeaderMenuShowI
            | BabylonHeaderMenuLogoCenterI
            | undefined
    ): void {
        this.headerData.set(headerData);
    }

    setHead(head: PageConfigHead | undefined): void {
        this.pageHead.set(head);
    }

    setLoadFooter(loadFooter: boolean): void {
        this.loadFooter.set(loadFooter);
    }

    setLoadHeaders(loadHeaders: boolean): void {
        this.loadHeaders.set(loadHeaders);
    }

    setEngineAlreadyExists(engineAlreadyExists: boolean): void {
        this.engineAlreadyExists.set(engineAlreadyExists);
    }

    setFooter(
        footer:
            | BodyComponent<BabylonStaticFooterI | BabylonStaticFooterV2I>
            | undefined
    ): void {
        this.footer.set(footer);
    }

    setEngineData(engineData: BabylonEngineI | undefined): void {
        this.engineData.set(engineData);
    }

    setSelectedHeader(selectedHeader: BabylonHeadersType): void {
        this.selectedHeader.set(selectedHeader);
    }

    setFooterLogosData(
        footerlogos: BodyComponent<BabylonFooterLogosI> | undefined
    ): void {
        this.footerLogos.set(footerlogos);
    }

    setFooterLinksData(
        footerlinks: BodyComponent<BabylonFooterLinksI> | undefined
    ): void {
        this.footerLinks.set(footerlinks);
    }

    setFooterContactData(
        footerContact: BodyComponent<BabylonFooterContactI> | undefined
    ): void {
        this.footerContact.set(footerContact);
    }

    setOffersListData(
        offersList: BodyComponent<BabylonOffersListI> | undefined
    ): void {
        this.offersList.set(offersList);
    }

    openGlobalModal(modalSvc: BabylonModalService, alias?: string): void {
        const globalModal = this.globalModal();
        if (globalModal) {
            if (alias) {
                for (let modal of globalModal) {
                    if (modal.alias?.toLowerCase() === alias.toLowerCase()) {
                        modalSvc.openReserveModal(modal.props);
                        return;
                    }
                }
            }
        }
    }
}
